import type { components, operations } from '@luxuryescapes/contract-svc-audit-v2/dist/server';

import { AuditLog, AuditLogResponse } from '~/types/responses';

import { json_headers, request } from './common';

const BASE_URL = '/api/audit/v2/events/fetch';

function getBasePath() {
  return window.configs.API_HOST;
}

// Re-export types from contract for external use
export type AuditEvent = components['schemas']['AuditEvent'];
export type StringFilter = components['schemas']['StringFilter'];
export type StringFilterWithoutIn = components['schemas']['StringFilterWithoutIn'];
export type DateFilter = components['schemas']['DateFilter'];
export type StringFilterMatch = keyof Exclude<StringFilter, string>;

// Request body type from the contract
export type SearchAuditEventsRequest = operations['searchAuditEvents']['requestBody']['content']['application/json'];

// Mapped audit log for grouping operations
export type MappedAuditLog = {
  logId: string;
  action: string;
  timestamp: string;
  userEmail: string;
  modelId: string;
  meta: AuditEvent['meta'];
  diff?: NonNullable<AuditEvent['model']>['diff'];
};

/**
 * Convert page/limit to skip/take
 */
function pageToSkipTake(page: number, limit: number): { skip: number; take: number } {
  return {
    skip: (page - 1) * limit,
    take: limit,
  };
}

/**
 * Search audit events with filters (POST request to v2 API)
 */
async function searchAuditEvents(searchRequest: SearchAuditEventsRequest): Promise<AuditLogResponse> {
  return request(`${getBasePath()}${BASE_URL}`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(searchRequest),
  }) as Promise<AuditLogResponse>;
}

/**
 * Get logs filtered by resource match using contains filter
 * Be aware that 'contains' is much slower than 'equals'
 */
export async function getLogsByResource(
  resource: string,
  match: StringFilterMatch,
  page = 1,
  limit = 50,
  totalCount = false,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      resource: { [match]: resource },
    },
  });
}

/**
 * Get logs filtered by actor email
 */
export async function getLogsByActorEmail(
  email: string,
  page = 1,
  limit = 50,
  totalCount = false,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      actor: { email },
    },
  });
}

/**
 * Get logs filtered by model type and ID
 */
export async function getLogsByModelAndId(
  modelType: string,
  modelId: string,
  page = 1,
  limit = 50,
  totalCount = false,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      model: {
        type: modelType,
        id: modelId,
      },
    },
  });
}

/**
 * Get logs filtered by model type only
 */
export async function getLogsByModel(
  modelType: string,
  page = 1,
  limit = 50,
  totalCount = false,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      model: { type: modelType },
    },
  });
}

/**
 * Get logs filtered by meta key and value
 */
export async function getLogsByMetaKeyValue(
  key: string,
  value: string,
  page = 1,
  limit = 50,
  totalCount = false,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      meta: {
        [key]: value,
      },
    },
  });
}

/**
 * Get logs filtered by service name
 */
export async function getLogsByService(
  serviceName: string,
  page = 1,
  limit = 50,
  totalCount = false,
): Promise<AuditLogResponse> {
  return searchAuditEvents({
    ...pageToSkipTake(page, limit),
    total_count: totalCount,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filter: {
      service: serviceName,
    },
  });
}

/**
 * Group audit logs by model ID for display purposes
 */
export function groupLogsByModelId(
  logs: AuditLog[],
  getIdForDelete: (log: AuditLog) => string,
): { [key: string]: MappedAuditLog[] } {
  return logs
    .flatMap((log) => {
      const actorEmail = log.actor.email ?? '';

      if (log.action === 'Delete') {
        return {
          logId: log.id,
          action: log.action,
          timestamp: log.timestamp,
          userEmail: actorEmail,
          meta: log.meta,
          modelId: getIdForDelete(log),
        };
      }

      if (!log.model) {
        return [];
      }

      return {
        logId: log.id,
        action: log.action,
        timestamp: log.timestamp,
        userEmail: actorEmail,
        meta: log.meta,
        modelId: log.model.id,
        diff: log.model.diff,
      };
    })
    .reduce((logMap, log) => {
      if (logMap[log.modelId]) {
        logMap[log.modelId].push(log);
      } else {
        logMap[log.modelId] = [log];
      }

      return logMap;
    }, {} as { [key: string]: MappedAuditLog[] });
}

/**
 * Group audit logs by model type for display purposes
 */
export function groupLogsByModelType(logs: AuditLog[]): { [key: string]: AuditLog[] } {
  const map: { [key: string]: AuditLog[] } = {};

  logs.forEach((log) => {
    if (log.model) {
      if (!map[log.model.type]) {
        map[log.model.type] = [];
      }
      map[log.model.type].push(log);
    }
  });

  return map;
}
